import { useLingui } from '@lingui/react/macro'
import {
  KNOWLEDGE_DOC_INTRA_DOC_SEARCH,
  useGeckoAnalytics,
} from 'features/analytics'
import { useIsEmbeddedMobileLayout } from 'features/embeddedSidebar/hooks/useIsEmbeddedMobileLayout'
import { DocumentViewerMetadataSplit } from 'features/knowledgeDocumentViewer/components/DocumentViewerMetadataSplit'
import { EvalDocumentUnavailablePanel } from 'features/knowledgeDocumentViewer/components/metadata/EvalDocumentUnavailablePanel'
import { CrossReferencePanel } from 'features/knowledgeDocumentViewer/components/sidebar/CrossReferencePanel'
import { KnowledgeDocumentSidebar } from 'features/knowledgeDocumentViewer/components/sidebar/KnowledgeDocumentSidebar'
import { PageThumbnailSidebar } from 'features/knowledgeDocumentViewer/components/thumbnailSidebar/PageThumbnailSidebar'
import { DocumentViewer } from 'features/knowledgeDocumentViewer/components/viewer/DocumentViewer'
import { getDocumentViewerInteractionProps } from 'features/knowledgeDocumentViewer/components/viewer/documentViewerInteractionProps'
import { CitedTextBar } from 'features/knowledgeDocumentViewer/components/viewer/search/CitedTextBar'
import { ConnectorLookupPanel } from 'features/knowledgeDocumentViewer/components/viewer/search/ConnectorLookupPanel'
import { DocReferencePreviewCard } from 'features/knowledgeDocumentViewer/components/viewer/search/DocReferencePreviewCard'
import { IntraDocSearchBar } from 'features/knowledgeDocumentViewer/components/viewer/search/IntraDocSearchBar'
import type { SearchHighlightData } from 'features/knowledgeDocumentViewer/components/viewer/search/SearchHighlightOverlay'
import type { DocumentViewerViewportState } from 'features/knowledgeDocumentViewer/components/viewer/shared/documentViewerViewport'
import { CITED_TEXT_FRAMING } from 'features/knowledgeDocumentViewer/components/viewer/shared/regionFraming'
import {
  Box as ViewerBox,
  DocumentViewerHandle,
  type FrameTarget,
} from 'features/knowledgeDocumentViewer/components/viewer/shared/types'
import { isEditableKeyTarget } from 'features/knowledgeDocumentViewer/components/viewer/shared/viewerHotkeys'
import { CrossReferenceContext } from 'features/knowledgeDocumentViewer/contexts/CrossReferenceContext'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useInstrumentSelectionOptional } from 'features/knowledgeDocumentViewer/contexts/InstrumentSelectionContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useAddDocumentToRecents } from 'features/knowledgeDocumentViewer/hooks/useAddDocumentToRecents'
import { useBoxClickHandler } from 'features/knowledgeDocumentViewer/hooks/useBoxClickHandler'
import { useCitedTextFrameTarget } from 'features/knowledgeDocumentViewer/hooks/useCitedTextFrameTarget'
import { useConnectorLookup } from 'features/knowledgeDocumentViewer/hooks/useConnectorLookup'
import { useCrossReference } from 'features/knowledgeDocumentViewer/hooks/useCrossReference'
import { useDocumentFullscreen } from 'features/knowledgeDocumentViewer/hooks/useDocumentFullscreen'
import { useFragmentHighlight } from 'features/knowledgeDocumentViewer/hooks/useFragmentHighlight'
import {
  type IntraDocSearchHit,
  type IntraDocSearchUiState,
  useIntraDocumentSearch,
} from 'features/knowledgeDocumentViewer/hooks/useIntraDocumentSearch'
import {
  LinkedDocumentsNavigationOptions,
  type LinkedDocumentsNavigationParams,
  useLinkedDocumentsNavigation,
} from 'features/knowledgeDocumentViewer/hooks/useLinkedDocumentsNavigation'
import { useMetadataPanelLayout } from 'features/knowledgeDocumentViewer/hooks/useMetadataPanelLayout'
import { useOffPageConnectorDocuments } from 'features/knowledgeDocumentViewer/hooks/useOffPageConnectorDocuments'
import { usePageNavigation } from 'features/knowledgeDocumentViewer/hooks/usePageNavigation'
import { useResetZoomAfterLayoutChange } from 'features/knowledgeDocumentViewer/hooks/useResetZoomAfterLayoutChange'
import { useSystemDocumentByBqId } from 'features/knowledgeDocumentViewer/hooks/useSystemDocumentsByBqIds'
import type {
  DocumentHistoryItem,
  FragmentHighlight,
  LotoPanelState,
  RegionHighlight,
} from 'features/knowledgeDocumentViewer/types'
import {
  FilterOption,
  TooltipOption,
} from 'features/knowledgeDocumentViewer/types'
import type { AliasHighlight } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import {
  DocumentViewerAssetType,
  type NormalizedBBox,
  shouldForceHideDocumentBoxes,
} from 'features/knowledgeDocumentViewer/types/ocrViewer'
import type {
  ConnectorLookupTarget,
  ConnectorOccurrence,
} from 'features/knowledgeDocumentViewer/utils/connectorLookup'
import type {
  CrossReferenceDocument,
  CrossReferenceOccurrence,
} from 'features/knowledgeDocumentViewer/utils/crossReference'
import { crossReferenceRegion } from 'features/knowledgeDocumentViewer/utils/crossReferenceContext'
import {
  docReferenceResolutionTexts,
  isSelfReference,
  normalizeReferenceText,
  resolveDocReferenceTargets,
} from 'features/knowledgeDocumentViewer/utils/docReferenceResolver'
import { shouldExitDocumentFullscreenOnKey } from 'features/knowledgeDocumentViewer/utils/documentFullscreen'
import {
  allCandidatesHaveRevisionCodes,
  getLatestCandidateByRevision,
  type NavigationTargetCandidate,
} from 'features/knowledgeDocumentViewer/utils/navigationTargetResolver'
import {
  getViewerPreferences,
  updateViewerPreferences,
} from 'features/knowledgeDocumentViewer/utils/viewerPreferencesStorage'
import { resolveBqDocumentId } from 'features/labeler/bqDocId'
import { LabelerDrawer } from 'features/labeler/components/LabelerDrawer'
import { LabelerProvider } from 'features/labeler/LabelerContext'
import { LotoDrawingProviders } from 'features/lotoReports/components/LotoDrawingProviders'
import { LotoLeaveDialog } from 'features/lotoReports/components/LotoLeaveDialog'
import { LotoPagePanel } from 'features/lotoReports/components/LotoPagePanel'
import type { LotoTabUpdate } from 'features/lotoReports/lotoPageNavigation'
import { useLotoDocumentTab } from 'features/lotoReports/useLotoDocumentTab'
import { useLotoLeaveGuards } from 'features/lotoReports/useLotoLeaveGuards'
import { useLotoPage, useLotoScope } from 'features/lotoReports/useLotoPage'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { PERMISSION_CANTILEVER_DOC_CHAT_CROSS_REFERENCE_VIEWER } from 'types'

import { DocumentFullscreenPortal } from './DocumentFullscreenPortal'
import { DocumentUnavailablePanel } from './DocumentUnavailablePanel'
import {
  DocumentContentRoot,
  DocumentFullscreenViewerShell,
  ThumbnailSidebarContainer,
  ViewerAndSidebarContainer,
  ViewerWithThumbnailsRow,
} from './knowledgeDocumentToolsStyles'
import { PhotoModePanel } from './photoMode/PhotoModePanel'
import { DocumentFullscreenOverlay } from './toolbar/DocumentFullscreenOverlay'
import { KnowledgeDocumentHeaderBar } from './toolbar/KnowledgeDocumentHeaderBar'

interface DocumentTabContentProps {
  tabId: string
  documentId: string
  pageNumber: number
  selectedBoxIds: string[]
  selectedSubjectUid?: string
  lotoPanel?: LotoPanelState
  scale: number
  isActiveTab: boolean
  setIsChatbotOpen: (open: boolean) => void
  onBoxContextMenuChange: (state: BoxContextMenuState | null) => void
  linkedDocsNavigationOptions?: Omit<
    LinkedDocumentsNavigationOptions,
    'offPageConnectorDocuments' | 'isLoadingOffPageConnector'
  >
  fragmentHighlight?: FragmentHighlight
  regionHighlight?: RegionHighlight
  initialIntraSearchState?: IntraDocSearchUiState
  onIntraSearchStateChange?: (state: IntraDocSearchUiState) => void
  initialViewport?: DocumentViewerViewportState | null
  onViewportChange?: (viewport: DocumentViewerViewportState) => void
}

export interface BoxContextMenuState {
  onCrossReference?: () => void
  position: { x: number; y: number } | null
  onClose: () => void
  onOpen: () => void
  onOpenInNewTab: () => void
  onAddToContext: () => void
  showNavigationOptions: boolean
  showAddToContext: boolean
}

export const DocumentTabContent: React.FC<DocumentTabContentProps> = ({
  tabId,
  documentId,
  pageNumber,
  selectedBoxIds,
  selectedSubjectUid,
  lotoPanel,
  scale,
  isActiveTab,
  setIsChatbotOpen,
  onBoxContextMenuChange,
  linkedDocsNavigationOptions,
  fragmentHighlight,
  regionHighlight,
  initialIntraSearchState,
  onIntraSearchStateChange,
  initialViewport = null,
  onViewportChange,
}) => {
  const {
    documentSignedURL,
    bqDocumentId,
    totalPages,
    documentTableRow,
    intraDocSearchDocumentId,
    sourceDocumentIdPresent,
    isSelectedDocumentUnavailable,
    docReferenceSubjects,
    documentSubjects,
    hasTimeSeriesData,
    getMatchedDatapoints,
  } = useDocumentTabDataContext()
  const instrumentSelection = useInstrumentSelectionOptional()
  const { siteID, showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()
  const { errorNotification, infoNotification } = useGeckoNotifications()
  const { t } = useLingui()
  const { trackEvent } = useGeckoAnalytics()
  const forceHideDocumentBoxes =
    shouldForceHideDocumentBoxes(showDocumentOcrBoxes)
  const { user, hasPermissions } = useGeckoPermissions()
  const canCrossReference = hasPermissions([
    PERMISSION_CANTILEVER_DOC_CHAT_CROSS_REFERENCE_VIEWER,
  ])
  useAddDocumentToRecents(documentId)
  const userId = user?.id

  const pdfUrl = documentSignedURL?.signed_url ?? ''

  const intraSearch = useIntraDocumentSearch(intraDocSearchDocumentId, siteID, {
    initialState: initialIntraSearchState,
    onStateChange: onIntraSearchStateChange,
  })
  const fragment = useFragmentHighlight(documentId, siteID, fragmentHighlight, {
    intraSearchIsOpen: intraSearch.isOpen,
  })

  const {
    activeTabId,
    tooltipOptions,
    setTooltipOptions,
    viewLayout,
    actionMode,
    isBrowserFullscreen,
    isDocumentFullscreen,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    isLabelerMode,
    setIsLabelerMode,
    isEvalSessionLocked,
    filterOptions,
    setFilterOptions,
    updateDocumentHistoryItem,
    pushToHistory,
    registerTabLeaveGuard,
    addDocumentTab,
    recordViewerAction,
    photoMode,
    setPhotoMode,
  } = useKnowledgeDocumentUXContext()
  const { exitDocumentFullscreen } = useDocumentFullscreen()
  const stackMetadataBelow = useIsEmbeddedMobileLayout()
  const metadataPanelOrientation = stackMetadataBelow
    ? 'vertical'
    : 'horizontal'
  const { defaultLayout: metadataPanelLayout, handleLayoutChanged } =
    useMetadataPanelLayout(metadataPanelOrientation)

  const crossReference = useCrossReference(
    siteID,
    bqDocumentId ?? documentId,
    pageNumber,
    isActiveTab && canCrossReference,
    userId
  )
  const { search: searchCrossReference, close: closeCrossReference } =
    crossReference
  const crossReferenceOccurrence = useRef<CrossReferenceOccurrence>()
  useLayoutEffect(() => {
    crossReferenceOccurrence.current = undefined
    if (isActiveTab) onBoxContextMenuChange(null)
  }, [
    documentId,
    pageNumber,
    isActiveTab,
    onBoxContextMenuChange,
    searchCrossReference,
  ])
  const startCrossReference = useCallback(
    (occurrence: CrossReferenceOccurrence) => {
      setIsSidebarCollapsed(false)
      if (isDocumentFullscreen) exitDocumentFullscreen()
      void searchCrossReference(occurrence)
    },
    [
      setIsSidebarCollapsed,
      isDocumentFullscreen,
      exitDocumentFullscreen,
      searchCrossReference,
    ]
  )
  const openCrossReferenceMenu = useCallback(
    (occurrence: CrossReferenceOccurrence, event: React.MouseEvent) => {
      if (
        !canCrossReference ||
        isEvalSessionLocked ||
        isLabelerMode ||
        !isActiveTab
      )
        return
      event.preventDefault()
      event.stopPropagation()
      crossReferenceOccurrence.current = occurrence
      onBoxContextMenuChange({
        position: { x: event.clientX, y: event.clientY },
        onClose: () => {
          onBoxContextMenuChange(null)
        },
        onOpen: () => {},
        onOpenInNewTab: () => {},
        onAddToContext: () => {},
        showNavigationOptions: false,
        showAddToContext: false,
        onCrossReference: () => startCrossReference(occurrence),
      })
    },
    [
      canCrossReference,
      isEvalSessionLocked,
      isLabelerMode,
      isActiveTab,
      onBoxContextMenuChange,
      startCrossReference,
    ]
  )
  const crossReferenceContext = useMemo(
    () =>
      canCrossReference
        ? {
            onContextMenu: openCrossReferenceMenu,
            marker: crossReference.state
              ? {
                  page: crossReference.state.occurrence.source_page_number,
                  point: crossReference.state.occurrence.point,
                  loading: crossReference.state.loading,
                }
              : undefined,
          }
        : undefined,
    [canCrossReference, openCrossReferenceMenu, crossReference.state]
  )
  const openCrossReferenceDocument = useCallback(
    (target: CrossReferenceDocument) => {
      pushToHistory(activeTabId, {
        type: 'document',
        documentId: target.document_id,
        pageNumber: target.page_number ?? 1,
        selectedBoxIds: [],
        query: '',
      })
    },
    [pushToHistory, activeTabId]
  )

  const [hoveredOffPageTexts, setHoveredOffPageTexts] = useState<
    string[] | undefined
  >()
  const [docReferencePreview, setDocReferencePreview] = useState<{
    label: string
    resolutionTexts: string[]
    x: number
    y: number
  } | null>(null)
  const connectorNewTab = useRef(false)
  const connectorClickKey = useRef<string>()
  const pendingDocReferenceClickRef = useRef<AliasHighlight | null>(null)

  const {
    systemDocumentEntries: offPageConnectorDocuments,
    isLoading: isLoadingOffPageConnector,
    error: offPageConnectorError,
  } = useOffPageConnectorDocuments(hoveredOffPageTexts, siteID)

  const connectorLookup = useConnectorLookup(
    siteID,
    bqDocumentId ?? documentId,
    pageNumber,
    userId,
    isActiveTab && canCrossReference
  )
  const {
    lookup: lookupConnector,
    cancel: cancelConnectorLookup,
    lastOccurrence,
  } = connectorLookup

  const navigateConnectorTarget = useCallback(
    (target: ConnectorLookupTarget) => {
      cancelConnectorLookup()
      const { document_id: destination, page_number: destinationPage } = target
      recordViewerAction(
        t`Followed connector to ${destination} page ${destinationPage}`
      )
      if (connectorNewTab.current) {
        addDocumentTab({
          documentId: target.document_id,
          pageNumber: target.page_number,
          selectedBoxIds: [],
        })
        return
      }
      pushToHistory(activeTabId, {
        type: 'document',
        documentId: target.document_id,
        pageNumber: target.page_number,
        selectedBoxIds: [],
        query: '',
        referenceOrigin: {
          documentId,
          label:
            documentTableRow?.drawing ?? documentTableRow?.title ?? documentId,
        },
      })
    },
    [
      t,
      cancelConnectorLookup,
      addDocumentTab,
      recordViewerAction,
      pushToHistory,
      activeTabId,
      documentId,
      documentTableRow,
    ]
  )

  const findConnectorDestination = useCallback(
    async (occurrence: ConnectorOccurrence) => {
      if (!canCrossReference) {
        const referenceLabel = occurrence.raw_text
        errorNotification(t`No document found for “${referenceLabel}”`)
        return
      }
      const response = await lookupConnector(occurrence)
      if (response?.status === 'resolved' && response.targets[0])
        navigateConnectorTarget(response.targets[0])
    },
    [
      canCrossReference,
      errorNotification,
      t,
      lookupConnector,
      navigateConnectorTarget,
    ]
  )

  const retryConnectorLookup = useCallback(() => {
    if (lastOccurrence.current)
      void findConnectorDestination(lastOccurrence.current)
  }, [lastOccurrence, findConnectorDestination])

  useLayoutEffect(() => {
    pendingDocReferenceClickRef.current = null
  }, [siteID, documentId, pageNumber, isActiveTab])

  const findDocReferenceSubject = useCallback(
    (highlight: AliasHighlight) =>
      docReferenceSubjects.find(
        (subject) => subject.subject_uid === highlight.subjectUid
      ),
    [docReferenceSubjects]
  )

  const {
    navigateToLinkedDocuments: navigateExistingLinkedDocuments,
    showLinkedDocsTable,
  } = useLinkedDocumentsNavigation({
    offPageConnectorDocuments,
    isLoadingOffPageConnector,
    ...linkedDocsNavigationOptions,
  })

  const handleBoxHover = useCallback((box: ViewerBox | null) => {
    if (pendingDocReferenceClickRef.current) return
    setHoveredOffPageTexts(
      box?.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR && box.label
        ? [box.label]
        : undefined
    )
  }, [])

  const handleDocReferenceHover = useCallback(
    (highlight: AliasHighlight | null, event?: React.MouseEvent) => {
      if (pendingDocReferenceClickRef.current) return
      const resolutionTexts = highlight
        ? docReferenceResolutionTexts(
            findDocReferenceSubject(highlight),
            highlight.label
          )
        : undefined
      setHoveredOffPageTexts(resolutionTexts)
      setDocReferencePreview(
        highlight && resolutionTexts && event
          ? {
              label: highlight.label,
              resolutionTexts,
              x: event.clientX,
              y: event.clientY,
            }
          : null
      )
    },
    [findDocReferenceSubject]
  )

  const navigateDocReference = useCallback(
    (highlight: AliasHighlight) => {
      setDocReferencePreview(null)
      const resolutionTexts = docReferenceResolutionTexts(
        findDocReferenceSubject(highlight),
        highlight.label
      )
      let referenceText = normalizeReferenceText(highlight.label)
      let targets: NavigationTargetCandidate[] = []
      for (const text of resolutionTexts) {
        targets = resolveDocReferenceTargets({
          referenceText: text,
          currentDocumentId: documentId,
          systemDocumentEntries: offPageConnectorDocuments,
        })
        if (targets.length > 0) {
          referenceText = text
          break
        }
      }
      if (targets.length === 0) {
        // Resolver excludes current document; title-block self-reference is identity, not a miss.
        const selfText = resolutionTexts.find((text) =>
          isSelfReference(text, documentTableRow?.drawing)
        )
        if (selfText) {
          infoNotification(
            t`"${selfText}" is this document's own drawing number`
          )
          return
        }
        const subject = findDocReferenceSubject(highlight)
        const bbox = highlight.bbox
        void findConnectorDestination({
          raw_text: highlight.label.slice(0, 300),
          context_text: [
            documentTableRow?.title,
            ...(subject?.aliases ?? [])
              .filter((a) => a.kind !== 'canonical_tag')
              .map((a) => a.value),
          ]
            .filter(Boolean)
            .join('; ')
            .slice(0, 800),
          source_page_number: highlight.page,
          source_bbox: [
            bbox.left,
            bbox.top,
            Math.min(1, bbox.left + bbox.width),
            Math.min(1, bbox.top + bbox.height),
          ],
        })
        return
      }

      const jumpTo = (target: NavigationTargetCandidate) => {
        recordViewerAction(
          // eslint-disable-next-line lingui/no-unlocalized-strings
          `Followed reference "${referenceText}" to ${target.documentId}`
        )
        pushToHistory(activeTabId, {
          type: 'document',
          documentId: target.documentId,
          pageNumber: target.pageNumber,
          selectedBoxIds: [],
          query: '',
          referenceOrigin: {
            documentId,
            label:
              documentTableRow?.drawing ??
              documentTableRow?.title ??
              documentId,
          },
        })
      }

      if (targets.length === 1) {
        jumpTo(targets[0])
        return
      }
      if (allCandidatesHaveRevisionCodes(targets)) {
        const latest = getLatestCandidateByRevision(targets)
        if (latest) {
          jumpTo(latest)
          return
        }
      }
      showLinkedDocsTable(
        targets.map((target) => target.documentId),
        referenceText,
        false
      )
    },
    [
      findDocReferenceSubject,
      documentId,
      offPageConnectorDocuments,
      findConnectorDestination,
      infoNotification,
      t,
      recordViewerAction,
      pushToHistory,
      activeTabId,
      documentTableRow,
      showLinkedDocsTable,
    ]
  )

  const handleDocReferenceClick = useCallback(
    (highlight: AliasHighlight) => {
      connectorNewTab.current = false
      const key = JSON.stringify([
        highlight.page,
        highlight.bbox,
        highlight.label,
      ])
      if (connectorClickKey.current !== key) cancelConnectorLookup()
      connectorClickKey.current = key
      pendingDocReferenceClickRef.current = highlight
      setHoveredOffPageTexts(
        docReferenceResolutionTexts(
          findDocReferenceSubject(highlight),
          highlight.label
        )
      )
    },
    [cancelConnectorLookup, findDocReferenceSubject]
  )

  useEffect(() => {
    const pending = pendingDocReferenceClickRef.current
    if (!pending || isLoadingOffPageConnector || !isActiveTab) return
    const texts = docReferenceResolutionTexts(
      findDocReferenceSubject(pending),
      pending.label
    )
    if (JSON.stringify(texts) !== JSON.stringify(hoveredOffPageTexts)) return
    pendingDocReferenceClickRef.current = null
    if (offPageConnectorError) {
      errorNotification(
        t`Could not load connected documents. Reload and try again.`
      )
      return
    }
    navigateDocReference(pending)
  }, [
    t,
    isLoadingOffPageConnector,
    offPageConnectorError,
    isActiveTab,
    hoveredOffPageTexts,
    findDocReferenceSubject,
    navigateDocReference,
    errorNotification,
  ])

  // Labeler has no zoom-pan wrapper; force single-page layout.
  const effectiveLayout =
    isLabelerMode || isEvalSessionLocked
      ? 'single'
      : totalPages <= 1
        ? 'single'
        : viewLayout

  useLayoutEffect(() => {
    if (totalPages > 1 && effectiveLayout === 'twoPage') {
      setTooltipOptions((prev) => {
        if (prev.includes(TooltipOption.ShowMultiplePages)) return prev
        return [
          ...prev,
          TooltipOption.ShowMultiplePages,
          TooltipOption.MultiPageTurner,
        ]
      })
    }
  }, [totalPages, effectiveLayout, setTooltipOptions])

  const viewerRef = useRef<DocumentViewerHandle>(null)

  const { document: labelerSystemDocument } = useSystemDocumentByBqId(
    isLabelerMode ? documentId : undefined
  )
  const labelerBqDocumentId = useMemo(
    () =>
      isLabelerMode ? resolveBqDocumentId(labelerSystemDocument) : undefined,
    [isLabelerMode, labelerSystemDocument]
  )
  const [labelerFrameBbox, setLabelerFrameBbox] =
    useState<NormalizedBBox | null>(null)
  useEffect(() => {
    setLabelerFrameBbox(null)
  }, [pageNumber, isLabelerMode])
  const handleCloseLabeler = useCallback(
    () => setIsLabelerMode(false),
    [setIsLabelerMode]
  )
  const isFirstLabelerRender = useRef(true)
  useEffect(() => {
    if (isFirstLabelerRender.current) {
      isFirstLabelerRender.current = false
      return
    }
    const timer = setTimeout(() => {
      requestAnimationFrame(() =>
        requestAnimationFrame(() => viewerRef.current?.resetZoom())
      )
    }, 350)
    return () => clearTimeout(timer)
  }, [isLabelerMode])

  const clearSelectedSubject = useCallback(
    () =>
      updateDocumentHistoryItem(activeTabId, { selectedSubjectUid: undefined }),
    [activeTabId, updateDocumentHistoryItem]
  )
  const handleExitPhotoMode = useCallback(
    () => setPhotoMode(false),
    [setPhotoMode]
  )

  const lotoScope = useLotoScope()
  const lotoPage = useLotoPage(lotoScope)
  const activeLotoReportId = isEvalSessionLocked
    ? undefined
    : lotoPanel?.activeReportId
  const visibleLotoPage =
    lotoPanel && !isEvalSessionLocked ? lotoPage : undefined
  const isLotoPanelShown =
    visibleLotoPage !== undefined &&
    !isSidebarCollapsed &&
    !crossReference.state
  const updateLotoTab = useCallback(
    (update: LotoTabUpdate) => updateDocumentHistoryItem(activeTabId, update),
    [activeTabId, updateDocumentHistoryItem]
  )
  const pushLotoHistory = useCallback(
    (item: DocumentHistoryItem) => pushToHistory(activeTabId, item),
    [activeTabId, pushToHistory]
  )
  const {
    session: lotoSession,
    leavePrompt: lotoLeavePrompt,
    addition: lotoAddition,
    select: selectLoto,
    close: closeLotoPanel,
    openPage: openLotoPage,
    visitPage: visitLotoPage,
    focusPoint: focusLotoPoint,
    drawing: lotoDrawing,
  } = useLotoDocumentTab({
    scope: lotoScope,
    panel: isEvalSessionLocked ? undefined : lotoPanel,
    systemDocumentId: lotoPage?.attachment.system_document_id,
    documentId,
    updateTab: updateLotoTab,
    addTab: addDocumentTab,
    pushTab: pushLotoHistory,
  })
  useLotoLeaveGuards({
    tabId,
    activeReportId: activeLotoReportId,
    prompt: lotoLeavePrompt,
    registerTabLeaveGuard,
  })
  useEffect(() => {
    if (isActiveTab && activeLotoReportId) setIsSidebarCollapsed(false)
  }, [isActiveTab, activeLotoReportId, setIsSidebarCollapsed])
  const toggleLotoPanel = useCallback(() => {
    if (isLotoPanelShown) return closeLotoPanel()
    closeCrossReference()
    updateDocumentHistoryItem(activeTabId, {
      lotoPanel: lotoPanel ?? {},
      selectedSubjectUid: undefined,
    })
    setIsSidebarCollapsed(false)
  }, [
    isLotoPanelShown,
    closeLotoPanel,
    closeCrossReference,
    activeTabId,
    lotoPanel,
    updateDocumentHistoryItem,
    setIsSidebarCollapsed,
  ])

  const navigateToLinkedDocuments = useCallback(
    (params: LinkedDocumentsNavigationParams) => {
      cancelConnectorLookup()
      pendingDocReferenceClickRef.current = null
      const handled = navigateExistingLinkedDocuments(params)
      const { ocrResult } = params
      if (
        handled ||
        ocrResult.type !== DocumentViewerAssetType.OFF_PAGE_CONNECTOR
      )
        return handled
      if (
        isLoadingOffPageConnector ||
        offPageConnectorError ||
        hoveredOffPageTexts?.[0] !== ocrResult.text
      )
        return false
      connectorNewTab.current = params.forceNewTab
      void findConnectorDestination({
        raw_text: (ocrResult.text ?? '').slice(0, 300),
        context_text: (documentTableRow?.title ?? '').slice(0, 800),
        source_page_number: ocrResult.pageNumber,
      })
      return true
    },
    [
      cancelConnectorLookup,
      navigateExistingLinkedDocuments,
      isLoadingOffPageConnector,
      offPageConnectorError,
      hoveredOffPageTexts,
      findConnectorDestination,
      documentTableRow,
    ]
  )

  const {
    boxContextMenuPosition,
    onBoxClick,
    onBoxAuxClick,
    onBoxContextMenu,
    handleBoxContextMenuClose,
    handleBoxContextMenuOpen,
    handleBoxContextMenuOpenInNewTab,
    handleBoxContextMenuAddToContext,
    contextMenuBoxHasTimeSeriesData,
    contextMenuBoxIsOffPageConnector,
    onMultipleBoxSelect,
    handlePageClick,
  } = useBoxClickHandler({
    hasTimeSeriesData,
    getMatchedDatapoints,
    navigateToLinkedDocuments,
    setIsChatbotOpen,
  })

  const prevMenuPosRef = useRef(boxContextMenuPosition)
  useEffect(() => {
    if (boxContextMenuPosition !== prevMenuPosRef.current) {
      prevMenuPosRef.current = boxContextMenuPosition
      if (boxContextMenuPosition) {
        onBoxContextMenuChange({
          position: boxContextMenuPosition,
          onCrossReference: canCrossReference
            ? () => {
                if (crossReferenceOccurrence.current)
                  startCrossReference(crossReferenceOccurrence.current)
              }
            : undefined,
          onClose: () => {
            handleBoxContextMenuClose()
          },
          onOpen: handleBoxContextMenuOpen,
          onOpenInNewTab: handleBoxContextMenuOpenInNewTab,
          onAddToContext: handleBoxContextMenuAddToContext,
          showNavigationOptions: contextMenuBoxIsOffPageConnector,
          showAddToContext: contextMenuBoxHasTimeSeriesData,
        })
      } else {
        onBoxContextMenuChange(null)
      }
    }
  })

  const multiPageTurner = tooltipOptions.includes(TooltipOption.MultiPageTurner)

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (isEvalSessionLocked) return
      // eslint-disable-next-line lingui/no-unlocalized-strings
      recordViewerAction(`Navigated to page ${newPage}`)
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPage,
        selectedBoxIds: [],
      })
    },
    [
      activeTabId,
      isEvalSessionLocked,
      recordViewerAction,
      updateDocumentHistoryItem,
    ]
  )

  const knownPageCount = isSelectedDocumentUnavailable
    ? null
    : documentTableRow?.numPages
  useEffect(() => {
    if (!isActiveTab || isEvalSessionLocked || !knownPageCount) return
    const clampedPage = Math.min(Math.max(pageNumber, 1), knownPageCount)
    if (clampedPage === pageNumber) return
    updateDocumentHistoryItem(activeTabId, {
      pageNumber: clampedPage,
      selectedBoxIds: [],
    })
  }, [
    activeTabId,
    isActiveTab,
    isEvalSessionLocked,
    knownPageCount,
    pageNumber,
    updateDocumentHistoryItem,
  ])

  usePageNavigation({
    currentPageNumber: pageNumber,
    totalPages,
    onPageChange: handlePageChange,
    pageStep: effectiveLayout === 'twoPage' && multiPageTurner ? 2 : 1,
    enabled: isActiveTab && !isEvalSessionLocked,
    viewLayout: effectiveLayout,
  })

  const handleZoomToBox = useCallback(
    (id: string) => {
      viewerRef.current?.zoomToBox(id, 2.5)
    },
    [viewerRef]
  )

  const showMultiplePages =
    effectiveLayout === 'twoPage' &&
    tooltipOptions.includes(TooltipOption.ShowMultiplePages)

  const [aliasFrameTarget, setAliasFrameTarget] = useState<FrameTarget>()
  useEffect(() => {
    if (!aliasFrameTarget) return
    const isVisible =
      aliasFrameTarget.pageNumber === pageNumber ||
      (showMultiplePages && aliasFrameTarget.pageNumber === pageNumber + 1)
    if (!photoMode || !isVisible) setAliasFrameTarget(undefined)
  }, [aliasFrameTarget, pageNumber, showMultiplePages, photoMode])

  const handleResetZoom = useCallback(() => {
    viewerRef.current?.resetZoom()
  }, [])

  const regionHit = useMemo<IntraDocSearchHit | null>(
    () =>
      regionHighlight && regionHighlight.boxes.length > 0
        ? {
            page_number: regionHighlight.pageNum,
            matched_text: '',
            bounding_boxes: regionHighlight.boxes,
          }
        : null,
    [regionHighlight]
  )

  const allHits = useMemo(() => {
    if (intraSearch.isOpen) return intraSearch.hits
    if (regionHit) return [regionHit]
    if (fragment.isActive && fragment.hit) return [fragment.hit]
    return []
  }, [
    intraSearch.isOpen,
    intraSearch.hits,
    regionHit,
    fragment.isActive,
    fragment.hit,
  ])

  const visiblePageHits = useMemo(
    () =>
      allHits.filter(
        (h) =>
          h.page_number === pageNumber ||
          (showMultiplePages && h.page_number === pageNumber + 1)
      ),
    [allHits, pageNumber, showMultiplePages]
  )

  const activeVisibleIndex = useMemo(() => {
    if (!intraSearch.isOpen || intraSearch.activeHitIndex < 0) return null
    const activeHit = intraSearch.hits[intraSearch.activeHitIndex]
    if (!activeHit) return null
    const idx = visiblePageHits.findIndex((h) => h === activeHit)
    return idx >= 0 ? idx : null
  }, [
    intraSearch.isOpen,
    intraSearch.activeHitIndex,
    intraSearch.hits,
    visiblePageHits,
  ])

  const searchHighlights: SearchHighlightData | undefined = useMemo(() => {
    if (visiblePageHits.length === 0) return undefined
    return {
      matches: visiblePageHits.map((h, i) => ({
        id: i,
        bboxes: h.bounding_boxes,
        isRightPage: showMultiplePages && h.page_number === pageNumber + 1,
      })),
      activeMatchId: activeVisibleIndex,
      color: intraSearch.isOpen ? 'yellow' : 'green',
    }
  }, [
    visiblePageHits,
    activeVisibleIndex,
    intraSearch.isOpen,
    showMultiplePages,
    pageNumber,
  ])

  const openSearchRef = useRef(intraSearch.open)
  openSearchRef.current = intraSearch.open

  const fragmentHighlightRef = useRef(fragmentHighlight)
  fragmentHighlightRef.current = fragmentHighlight
  const regionHighlightRef = useRef(regionHighlight)
  regionHighlightRef.current = regionHighlight

  useEffect(() => {
    if (
      intraSearch.isOpen &&
      (fragmentHighlightRef.current || regionHighlightRef.current)
    ) {
      updateDocumentHistoryItem(activeTabId, {
        fragmentHighlight: undefined,
        regionHighlight: undefined,
      })
    }
  }, [intraSearch.isOpen, activeTabId, updateDocumentHistoryItem])

  // Clear region highlight only after the viewer framed it — pageNumber alone
  // is unreliable during continuous-scroll travel.
  const regionFramedRef = useRef<IntraDocSearchHit | null>(null)
  const handleRegionFramed = useCallback(() => {
    regionFramedRef.current = regionHit
  }, [regionHit])
  useEffect(() => {
    if (!regionHit || regionFramedRef.current !== regionHit) return
    const isVisible =
      regionHit.page_number === pageNumber ||
      (showMultiplePages && regionHit.page_number === pageNumber + 1)
    if (!isVisible) {
      updateDocumentHistoryItem(activeTabId, {
        regionHighlight: undefined,
      })
    }
  }, [
    regionHit,
    pageNumber,
    showMultiplePages,
    activeTabId,
    updateDocumentHistoryItem,
  ])

  useEffect(() => {
    if (!fragmentHighlightRef.current || !fragment.isActive) return
    const fragPage = fragmentHighlightRef.current.pageNum
    const isVisible =
      fragPage === pageNumber ||
      (showMultiplePages && fragPage === pageNumber + 1)
    if (!isVisible) {
      updateDocumentHistoryItem(activeTabId, {
        fragmentHighlight: undefined,
      })
    }
  }, [
    pageNumber,
    showMultiplePages,
    fragment.isActive,
    activeTabId,
    updateDocumentHistoryItem,
  ])

  const lastNavigatedHitRef = useRef<typeof intraSearch.activeHit>(null)
  const restoreNavQueryRef = useRef(
    initialIntraSearchState?.isOpen
      ? initialIntraSearchState.query || null
      : null
  )
  const pageNumberRef = useRef(pageNumber)
  pageNumberRef.current = pageNumber
  const showMultiplePagesRef = useRef(showMultiplePages)
  showMultiplePagesRef.current = showMultiplePages

  const [persistedThumbPrefs] = useState(() =>
    userId ? getViewerPreferences(userId) : null
  )
  const [isThumbnailSidebarOpen, setIsThumbnailSidebarOpen] = useState(
    persistedThumbPrefs?.isThumbnailSidebarOpen ?? true
  )
  const [thumbnailSidebarWidth, setThumbnailSidebarWidth] = useState<
    number | null
  >(persistedThumbPrefs?.thumbnailSidebarWidth ?? null)

  const isMultiPage = totalPages > 1
  const effectiveThumbnailSidebarOpen = isMultiPage && isThumbnailSidebarOpen

  const handleToggleThumbnailSidebar = useCallback(() => {
    if (!isMultiPage) return
    setIsThumbnailSidebarOpen((prev) => !prev)
  }, [isMultiPage])

  const handleThumbnailPageSelect = useCallback(
    (newPage: number) => {
      if (isEvalSessionLocked) return
      // eslint-disable-next-line lingui/no-unlocalized-strings
      recordViewerAction(`Selected thumbnail for page ${newPage}`)
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: newPage,
        selectedBoxIds: [],
      })
    },
    [
      activeTabId,
      isEvalSessionLocked,
      recordViewerAction,
      updateDocumentHistoryItem,
    ]
  )

  const handleThumbnailResizeEnd = useCallback(() => {
    handleResetZoom()
  }, [handleResetZoom])

  useResetZoomAfterLayoutChange(isBrowserFullscreen, handleResetZoom)
  useResetZoomAfterLayoutChange(isDocumentFullscreen, handleResetZoom)
  useResetZoomAfterLayoutChange(isSidebarCollapsed, handleResetZoom)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isActiveTab || isEvalSessionLocked) return

      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault()
        openSearchRef.current()
        return
      }

      if (isEditableKeyTarget(document.activeElement)) return

      if (event.key === 'Escape') {
        if (
          shouldExitDocumentFullscreenOnKey({
            isDocumentFullscreen,
            key: event.key,
          })
        ) {
          event.preventDefault()
          exitDocumentFullscreen()
        } else if (fragment.isActive || regionHighlightRef.current) {
          event.preventDefault()
          updateDocumentHistoryItem(activeTabId, {
            fragmentHighlight: undefined,
            regionHighlight: undefined,
          })
        } else if (selectedBoxIds.length > 0) {
          event.preventDefault()
          updateDocumentHistoryItem(activeTabId, { selectedBoxIds: [] })
        }
      } else if (event.key === 'b' || event.key === 'B') {
        event.preventDefault()
        if (forceHideDocumentBoxes) return

        const newFilter =
          filterOptions === FilterOption.ShowConfirmed
            ? FilterOption.RemoveBoxes
            : FilterOption.ShowConfirmed
        // eslint-disable-next-line lingui/no-unlocalized-strings
        recordViewerAction(`Changed box filter to ${newFilter}`)
        setFilterOptions(newFilter)
      } else if (event.key === 't' || event.key === 'T') {
        event.preventDefault()
        const showingTooltips = tooltipOptions.includes(
          TooltipOption.ShowTooltips
        )
        const boxesHidden = filterOptions === FilterOption.RemoveBoxes
        if (!boxesHidden) {
          const newOptions = showingTooltips
            ? tooltipOptions.filter((opt) => opt !== TooltipOption.ShowTooltips)
            : [...tooltipOptions, TooltipOption.ShowTooltips]
          recordViewerAction(
            showingTooltips
              ? // eslint-disable-next-line lingui/no-unlocalized-strings
                'Hid document tooltips'
              : // eslint-disable-next-line lingui/no-unlocalized-strings
                'Showed document tooltips'
          )
          setTooltipOptions(newOptions)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [
    isActiveTab,
    isEvalSessionLocked,
    activeTabId,
    fragment.isActive,
    filterOptions,
    forceHideDocumentBoxes,
    isDocumentFullscreen,
    selectedBoxIds.length,
    setFilterOptions,
    tooltipOptions,
    setTooltipOptions,
    recordViewerAction,
    updateDocumentHistoryItem,
    exitDocumentFullscreen,
  ])

  const navigateToHit = useCallback(
    (hit: IntraDocSearchHit) => {
      if (hit === lastNavigatedHitRef.current) return
      lastNavigatedHitRef.current = hit

      const currentPage = pageNumberRef.current
      const isMultiPage = showMultiplePagesRef.current
      const alreadyVisible =
        hit.page_number === currentPage ||
        (isMultiPage && hit.page_number === currentPage + 1)

      if (!alreadyVisible) {
        recordViewerAction(
          // eslint-disable-next-line lingui/no-unlocalized-strings
          `Navigated to search match on page ${hit.page_number}`
        )
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: hit.page_number,
          selectedBoxIds: [],
        })
      }

      if (!isMultiPage && hit.bounding_boxes.length > 0) {
        requestAnimationFrame(() => {
          viewerRef.current?.panToRegion?.(hit.bounding_boxes[0])
        })
      }
    },
    [activeTabId, recordViewerAction, updateDocumentHistoryItem]
  )

  const handleFocusAlias = useCallback(
    (highlight: AliasHighlight, event?: React.MouseEvent) => {
      closeCrossReference()
      if (instrumentSelection && !photoMode) {
        const subject = documentSubjects.find(
          (candidate) => candidate.subject_uid === highlight.subjectUid
        )
        if (subject?.kind === 'instrument') {
          const instrument = {
            key: subject.asset_id,
            label: subject.name || highlight.label,
            sourceLabel: highlight.label,
            datapointIds: getMatchedDatapoints(highlight.label).map(
              (dp) => dp.id
            ),
            assetId: subject.asset_id,
            subjectUid: subject.subject_uid,
            documentId,
            boxIds: [],
          }
          if (event?.shiftKey) {
            instrumentSelection.toggle(instrument)
          } else {
            instrumentSelection.select(instrument)
          }
          return
        }
      }
      if (photoMode) {
        setAliasFrameTarget({
          bbox: highlight.bbox,
          pageNumber: highlight.page,
          framing: CITED_TEXT_FRAMING,
        })
      } else {
        requestAnimationFrame(() => {
          viewerRef.current?.panToRegion?.(highlight.bbox)
        })
      }
      updateDocumentHistoryItem(activeTabId, {
        ...(highlight.page !== pageNumber && { pageNumber: highlight.page }),
        selectedSubjectUid: highlight.subjectUid,
        ...(!activeLotoReportId && { lotoPanel: undefined }),
      })
      if (!photoMode) setIsSidebarCollapsed(false)
    },
    [
      photoMode,
      closeCrossReference,
      activeTabId,
      activeLotoReportId,
      pageNumber,
      updateDocumentHistoryItem,
      setIsSidebarCollapsed,
      instrumentSelection,
      documentSubjects,
      getMatchedDatapoints,
      documentId,
    ]
  )

  const lastOpenedDocumentRef = useRef<string | null>(null)

  useEffect(() => {
    if (!documentId) return
    if (lastOpenedDocumentRef.current === documentId) return
    lastOpenedDocumentRef.current = documentId
    // eslint-disable-next-line lingui/no-unlocalized-strings
    recordViewerAction(`Opened document ${documentId} on page ${pageNumber}`)
  }, [documentId, pageNumber, recordViewerAction])

  const searchActionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const lastRecordedSearchQueryRef = useRef('')

  useEffect(() => {
    const query = intraSearch.query.trim()
    if (
      !intraSearch.isOpen ||
      !query ||
      query === lastRecordedSearchQueryRef.current
    ) {
      return
    }

    if (searchActionTimeoutRef.current) {
      clearTimeout(searchActionTimeoutRef.current)
    }

    searchActionTimeoutRef.current = setTimeout(() => {
      lastRecordedSearchQueryRef.current = query
      // eslint-disable-next-line lingui/no-unlocalized-strings
      recordViewerAction(`Searched within document for "${query}"`)
      trackEvent(KNOWLEDGE_DOC_INTRA_DOC_SEARCH.title, {
        system_document_entry_id: documentId,
        source_document_id_present: sourceDocumentIdPresent,
      })
    }, 800)

    return () => {
      if (searchActionTimeoutRef.current) {
        clearTimeout(searchActionTimeoutRef.current)
      }
    }
  }, [
    documentId,
    intraSearch.isOpen,
    intraSearch.query,
    recordViewerAction,
    sourceDocumentIdPresent,
    trackEvent,
  ])

  useEffect(() => {
    if (!intraSearch.activeHit || !intraSearch.isOpen) {
      lastNavigatedHitRef.current = null
      return
    }
    if (
      restoreNavQueryRef.current !== null &&
      restoreNavQueryRef.current === intraSearch.query
    ) {
      restoreNavQueryRef.current = null
      lastNavigatedHitRef.current = intraSearch.activeHit
      return
    }
    navigateToHit(intraSearch.activeHit)
  }, [
    intraSearch.activeHit,
    intraSearch.isOpen,
    intraSearch.query,
    navigateToHit,
  ])

  const handleDismissCitedText = useCallback(() => {
    updateDocumentHistoryItem(activeTabId, { fragmentHighlight: undefined })
  }, [activeTabId, updateDocumentHistoryItem])

  const { citedFragmentHit, frameTarget: hitFrameTarget } =
    useCitedTextFrameTarget({
      regionHit,
      fragmentHighlight,
      fragment,
      intraSearchIsOpen: intraSearch.isOpen,
    })

  useEffect(() => {
    setAliasFrameTarget(undefined)
  }, [hitFrameTarget])

  useEffect(() => {
    if (!fragment.hit || !fragment.isActive || intraSearch.isOpen) return
    if (citedFragmentHit) return
    navigateToHit(fragment.hit)
  }, [
    citedFragmentHit,
    fragment.hit,
    fragment.isActive,
    intraSearch.isOpen,
    navigateToHit,
  ])

  const regionNavRef = useRef<IntraDocSearchHit | null>(null)
  useEffect(() => {
    if (!regionHit || intraSearch.isOpen) return
    if (regionNavRef.current === regionHit) return
    regionNavRef.current = regionHit

    const currentPage = pageNumberRef.current
    const alreadyVisible =
      regionHit.page_number === currentPage ||
      (showMultiplePagesRef.current &&
        regionHit.page_number === currentPage + 1)
    if (!alreadyVisible) {
      recordViewerAction(
        // eslint-disable-next-line lingui/no-unlocalized-strings
        `Navigated to cited region on page ${regionHit.page_number}`
      )
      updateDocumentHistoryItem(activeTabId, {
        pageNumber: regionHit.page_number,
        selectedBoxIds: [],
      })
    }
  }, [
    regionHit,
    intraSearch.isOpen,
    activeTabId,
    recordViewerAction,
    updateDocumentHistoryItem,
  ])

  const frameTarget = useMemo<FrameTarget | undefined>(() => {
    if (isLabelerMode && labelerFrameBbox) {
      return { bbox: labelerFrameBbox, pageNumber }
    }
    return aliasFrameTarget ?? hitFrameTarget
  }, [
    isLabelerMode,
    labelerFrameBbox,
    pageNumber,
    aliasFrameTarget,
    hitFrameTarget,
  ])

  useEffect(() => {
    if (!userId || !isMultiPage) return
    const timer = setTimeout(() => {
      updateViewerPreferences(userId, {
        isThumbnailSidebarOpen,
        thumbnailSidebarWidth,
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [userId, isMultiPage, isThumbnailSidebarOpen, thumbnailSidebarWidth])

  // Deep link to a document outside the selected pipeline-config scope.
  if (isSelectedDocumentUnavailable) {
    return (
      <DocumentContentRoot role='presentation'>
        {isEvalSessionLocked ? (
          <EvalDocumentUnavailablePanel
            documentId={documentId}
            siteID={siteID}
          />
        ) : (
          <DocumentUnavailablePanel documentId={documentId} siteID={siteID} />
        )}
      </DocumentContentRoot>
    )
  }

  const isActiveDocumentFullscreen = isActiveTab && isDocumentFullscreen
  const showMetadataPanel =
    (isEvalSessionLocked || (!isLabelerMode && !isSidebarCollapsed)) &&
    !isActiveDocumentFullscreen &&
    !photoMode
  const showThumbnailSidebar =
    isMultiPage &&
    isThumbnailSidebarOpen &&
    !isActiveDocumentFullscreen &&
    !isEvalSessionLocked

  const documentViewerInteractionProps = getDocumentViewerInteractionProps(
    isEvalSessionLocked,
    {
      searchHighlights,
      frameTarget,
      highlightBoxIds: selectedBoxIds,
      onBoxClick,
      onBoxAuxClick,
      onBoxContextMenu,
      onBoxHover: handleBoxHover,
      onMultipleBoxSelect,
      onAliasClick: handleFocusAlias,
      onDocReferenceClick: handleDocReferenceClick,
      onDocReferenceHover: handleDocReferenceHover,
      showTooltips: tooltipOptions.includes(TooltipOption.ShowTooltips),
    }
  )

  const documentViewerContent = (
    <DocumentFullscreenPortal isDocumentFullscreen={isActiveDocumentFullscreen}>
      <DocumentFullscreenViewerShell
        onContextMenu={(event) => {
          const target = event.target instanceof Element ? event.target : null
          // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM selector, not UI text.
          if (target?.closest('button, input, textarea, [role="menu"]')) return
          const image = Array.from(
            // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM selector, not UI text.
            event.currentTarget.querySelectorAll('img,canvas')
          ).find((image) => {
            const r = image.getBoundingClientRect()
            return (
              r.width > 0 &&
              r.height > 0 &&
              event.clientX >= r.left &&
              event.clientX <= r.right &&
              event.clientY >= r.top &&
              event.clientY <= r.bottom
            )
          })
          if (!image) return
          const bounds = image.getBoundingClientRect()
          const x = (event.clientX - bounds.left) / bounds.width
          const y = (event.clientY - bounds.top) / bounds.height
          const clickedPage =
            // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM selector, not UI text.
            Number(image.closest('[data-page]')?.getAttribute('data-page')) ||
            pageNumber
          openCrossReferenceMenu(
            {
              selected_text: '',
              source_page_number: clickedPage,
              point: [x, y],
              source_bbox: crossReferenceRegion(x, y),
            },
            event
          )
        }}
        $isDocumentFullscreen={isActiveDocumentFullscreen}>
        {intraSearch.isOpen && !isEvalSessionLocked ? (
          <IntraDocSearchBar searchState={intraSearch} />
        ) : null}
        {fragmentHighlight?.quotes && !intraSearch.isOpen ? (
          <CitedTextBar
            isLocating={fragment.isLoading}
            isLocated={fragment.hit !== null}
            onDismiss={handleDismissCitedText}
          />
        ) : null}
        {connectorLookup.state && (
          <ConnectorLookupPanel
            state={connectorLookup.state}
            onSelect={navigateConnectorTarget}
            onClose={cancelConnectorLookup}
            onRetry={retryConnectorLookup}
          />
        )}
        <DocumentViewer
          ref={viewerRef}
          documentId={documentId}
          isActiveTab={isActiveTab}
          pageNumber={pageNumber}
          pdfUrl={pdfUrl}
          scale={scale}
          totalPages={totalPages}
          showMultiplePages={showMultiplePages}
          viewLayout={effectiveLayout}
          onFrameApplied={handleRegionFramed}
          onPageChange={handlePageChange}
          initialViewport={initialViewport}
          onViewportChange={onViewportChange}
          actionMode={actionMode}
          {...documentViewerInteractionProps}
        />
        {isActiveDocumentFullscreen ? (
          <DocumentFullscreenOverlay onExit={exitDocumentFullscreen} />
        ) : null}
      </DocumentFullscreenViewerShell>
    </DocumentFullscreenPortal>
  )
  const documentViewerPane = (
    <LotoDrawingProviders drawing={lotoDrawing}>
      {documentViewerContent}
    </LotoDrawingProviders>
  )

  const thumbnailSidebar = (
    <ThumbnailSidebarContainer
      $isOpen={showThumbnailSidebar}
      $width={thumbnailSidebarWidth}>
      <PageThumbnailSidebar
        totalPages={totalPages}
        currentPage={pageNumber}
        isOpen={showThumbnailSidebar}
        sidebarWidth={thumbnailSidebarWidth}
        pdfUrl={pdfUrl || undefined}
        onPageSelect={handleThumbnailPageSelect}
        onWidthChange={setThumbnailSidebarWidth}
        onResizeEnd={handleThumbnailResizeEnd}
        handleResetZoom={handleResetZoom}
      />
    </ThumbnailSidebarContainer>
  )

  return (
    <CrossReferenceContext.Provider value={crossReferenceContext}>
      <LabelerProvider
        active={isLabelerMode && !isEvalSessionLocked}
        siteId={siteID}
        bqDocumentId={labelerBqDocumentId}
        pageNumber={pageNumber}
        onZoomRequest={setLabelerFrameBbox}>
        <DocumentContentRoot role='presentation' onClick={handlePageClick}>
          {isActiveDocumentFullscreen ? null : (
            <KnowledgeDocumentHeaderBar
              compact={stackMetadataBelow}
              isThumbnailSidebarOpen={effectiveThumbnailSidebarOpen}
              onToggleThumbnailSidebar={handleToggleThumbnailSidebar}
              isMultiPage={isMultiPage}
              onOpenFindInDocument={intraSearch.open}
              isLotoPanelOpen={isLotoPanelShown}
              onToggleLotoPanel={lotoPage ? toggleLotoPanel : undefined}
            />
          )}
          <ViewerAndSidebarContainer>
            {stackMetadataBelow ? null : thumbnailSidebar}
            <DocumentViewerMetadataSplit
              showMetadataPanel={showMetadataPanel}
              compactMetadata={stackMetadataBelow && !isEvalSessionLocked}
              defaultLayout={metadataPanelLayout}
              onLayoutChanged={handleLayoutChanged}
              orientation={metadataPanelOrientation}
              viewer={
                stackMetadataBelow ? (
                  <ViewerWithThumbnailsRow>
                    {thumbnailSidebar}
                    {documentViewerPane}
                  </ViewerWithThumbnailsRow>
                ) : (
                  documentViewerPane
                )
              }
              metadata={
                <KnowledgeDocumentSidebar
                  handleZoomToBox={handleZoomToBox}
                  placement={stackMetadataBelow ? 'bottom' : 'side'}
                  selectedSubjectUid={selectedSubjectUid}
                  clearSelectedSubject={clearSelectedSubject}
                  lotoPanel={
                    visibleLotoPage ? (
                      <LotoPagePanel
                        key={JSON.stringify(visibleLotoPage.attachment)}
                        page={visibleLotoPage}
                        session={lotoSession}
                        addition={lotoAddition}
                        onClose={closeLotoPanel}
                        onSelect={selectLoto}
                        onOpenPage={openLotoPage}
                        onVisitPage={visitLotoPage}
                        onFocusPoint={focusLotoPoint}
                      />
                    ) : undefined
                  }
                  crossReferencePanel={
                    crossReference.state ? (
                      <CrossReferencePanel
                        state={crossReference.state}
                        onClose={crossReference.close}
                        onRetry={() => {
                          if (crossReference.state)
                            void crossReference.search(
                              crossReference.state.occurrence
                            )
                        }}
                        onSelect={openCrossReferenceDocument}
                        onSelectCandidate={crossReference.selectCandidate}
                      />
                    ) : undefined
                  }
                />
              }
            />

            {docReferencePreview && !isEvalSessionLocked ? (
              <DocReferencePreviewCard
                referenceLabel={docReferencePreview.label}
                resolutionTexts={docReferencePreview.resolutionTexts}
                currentDocumentId={documentId}
                systemDocumentEntries={offPageConnectorDocuments}
                isLoadingEntries={isLoadingOffPageConnector}
                siteId={siteID}
                position={docReferencePreview}
              />
            ) : null}

            {isLabelerMode &&
            !isActiveDocumentFullscreen &&
            !isEvalSessionLocked ? (
              <LabelerDrawer
                onClose={handleCloseLabeler}
                onResetZoom={handleResetZoom}
              />
            ) : null}
          </ViewerAndSidebarContainer>
          {photoMode && !isActiveDocumentFullscreen ? (
            <PhotoModePanel
              siteId={siteID}
              subjectUid={selectedSubjectUid}
              onExit={handleExitPhotoMode}
            />
          ) : null}
        </DocumentContentRoot>
        <LotoLeaveDialog prompt={lotoLeavePrompt} />
      </LabelerProvider>
    </CrossReferenceContext.Provider>
  )
}
